import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Grid, Paper, Typography, Table, TableHead, TableRow, TableCell, TableBody, Stack, Avatar, Chip, TextField, MenuItem, LinearProgress, CircularProgress, Collapse, IconButton } from '@mui/material';
import SearchIcon        from '@mui/icons-material/Search';
import FolderOpenIcon    from '@mui/icons-material/FolderOpen';
import KeyboardArrowUpIcon   from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import * as XLSX         from 'xlsx';
import jsPDF             from 'jspdf';
import autoTable         from 'jspdf-autotable';
import apiService        from '../../services/apiServices';
import PageHeader        from '../common/PageHeader';
import { ReportPage, ExportMenu } from './ReportKit';

const todayStr   = () => new Date().toISOString().slice(0, 10);
const monthStart = () => { const d = new Date(); d.setDate(1); return d.toISOString().slice(0, 10); };

function fmtHHMM(min) {
  const m = Math.max(0, Math.round(min));
  return `${Math.floor(m / 60)}:${('0' + (m % 60)).slice(-2)}`;
}
function initials(name) {
  return (name || '').split(' ').filter(Boolean).map(n => n[0]).join('').toUpperCase().slice(0, 2);
}

function ProjectRow({ proj, workers, t }) {
  const [open, setOpen] = useState(false);
  const workerById = id => workers.find(w => String(w.Id || w.id) === String(id));
  const pct = proj.budgetMin > 0 ? Math.min(100, Math.round((proj.totalMin / proj.budgetMin) * 100)) : 0;

  return (
    <>
      <TableRow hover sx={{ bgcolor: 'grey.50', '& > *': { borderBottom: 'unset' } }}>
        <TableCell sx={{ width: 36, pl: 1 }}>
          <IconButton size="small" onClick={() => setOpen(o => !o)}>
            {open ? <KeyboardArrowUpIcon sx={{ fontSize: 16 }} /> : <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: proj.color || '#3E8FA3', flexShrink: 0 }} />
            <Typography variant="body2" fontWeight={700}>{proj.name || '—'}</Typography>
          </Stack>
        </TableCell>
        <TableCell sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.85rem' }}>{fmtHHMM(proj.totalMin)}</TableCell>
        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.82rem', color: 'text.secondary' }}>
          {proj.budgetMin > 0 ? fmtHHMM(proj.budgetMin) : '—'}
        </TableCell>
        <TableCell sx={{ minWidth: 130 }}>
          {proj.budgetMin > 0 ? (
            <Stack spacing={0.25}>
              <Typography variant="caption" fontWeight={700}
                color={pct > 100 ? 'error.main' : pct >= 80 ? 'warning.main' : 'success.main'}>{pct}%</Typography>
              <LinearProgress variant="determinate" value={Math.min(100, pct)}
                color={pct > 100 ? 'error' : pct >= 80 ? 'warning' : 'success'}
                sx={{ height: 5, borderRadius: 4 }} />
            </Stack>
          ) : <Typography variant="caption" color="text.disabled">{t('report.noBudget')}</Typography>}
        </TableCell>
        <TableCell align="center">
          <Chip size="small" label={`${proj.workers} ${t('report.workers')}`} sx={{ fontSize: '0.72rem' }} />
        </TableCell>
        <TableCell align="center" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>{proj.entries}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={7} sx={{ py: 0, bgcolor: '#F8FAFC' }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ py: 1, px: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>{t('report.worker')}</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>{t('report.hours')}</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>{t('report.entries')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(proj.byWorker).sort((a, b) => b[1].min - a[1].min).map(([wid, wd]) => {
                    const w = workerById(wid);
                    const name = w ? `${w.FirstName || ''} ${w.LastName || ''}`.trim() : `#${wid}`;
                    return (
                      <TableRow key={wid}>
                        <TableCell>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <Avatar sx={{ width: 20, height: 20, fontSize: '0.6rem', bgcolor: proj.color || '#3E8FA3' }}>{initials(name)}</Avatar>
                            <Typography variant="caption">{name}</Typography>
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem', fontWeight: 600 }}>{fmtHHMM(wd.min)}</TableCell>
                        <TableCell sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>{wd.count}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function ProjectHoursReport() {
  const { t } = useTranslation();
  const [dateFrom,    setDateFrom]    = useState(monthStart());
  const [dateTo,      setDateTo]      = useState(todayStr());
  const [fProject,    setFProject]    = useState('');
  const [loading,     setLoading]     = useState(false);
  const [entries,     setEntries]     = useState([]);
  const [projects,    setProjects]    = useState([]);
  const [workers,     setWorkers]     = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, wRes, eRes] = await Promise.all([
        apiService().get('projects'),
        apiService().get('workers'),
        apiService().get('timeEntries'),
      ]);
      if (pRes.success) setProjects(pRes.data || []);
      if (wRes.success) setWorkers(wRes.data || []);
      if (eRes.success) setEntries(eRes.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const projById = useCallback(id => projects.find(p => String(p.id || p.Id) === String(id)), [projects]);

  const rows = useMemo(() => {
    const map = {};
    entries.forEach(e => {
      if (!e.project_id) return;
      if (e.date && (e.date < dateFrom || e.date > dateTo)) return;
      if (fProject && String(e.project_id) !== String(fProject)) return;
      const pid = String(e.project_id);
      if (!map[pid]) map[pid] = { pid, totalMin: 0, entries: 0, byWorker: {} };
      map[pid].totalMin += (e.duration_seconds || 0) / 60;
      map[pid].entries++;
      const wid = String(e.worker_id || '');
      if (wid) {
        if (!map[pid].byWorker[wid]) map[pid].byWorker[wid] = { min: 0, count: 0 };
        map[pid].byWorker[wid].min   += (e.duration_seconds || 0) / 60;
        map[pid].byWorker[wid].count++;
      }
    });
    return Object.values(map).map(r => {
      const p = projById(r.pid);
      return {
        ...r,
        name:      p ? (p.name || p.Name || '—') : `Projekt #${r.pid}`,
        // Pa ngjyrë të vetën → marka, jo një indigo e zgjedhur rastësisht.
        color:     (p && (p.color || p.Color)) || '#3E8FA3',
        budgetMin: p ? (+(p.BudgetHours || p.budget_hours || 0)) * 60 : 0,
        workers:   Object.keys(r.byWorker).length,
      };
    }).sort((a, b) => b.totalMin - a.totalMin);
  }, [entries, projects, dateFrom, dateTo, fProject, projById]);

  const stats = useMemo(() => ({
    projects: rows.length,
    totalH:   rows.reduce((s, r) => s + r.totalMin, 0),
    budgetH:  rows.reduce((s, r) => s + r.budgetMin, 0),
    entries:  rows.reduce((s, r) => s + r.entries, 0),
  }), [rows]);

  const HEADERS = [t('report.project'), t('report.totalHours'), t('report.budgetHours'), t('report.progress'), t('report.workers'), t('report.entries')];
  const expRows  = () => rows.flatMap(r => [
    [r.name, fmtHHMM(r.totalMin), r.budgetMin > 0 ? fmtHHMM(r.budgetMin) : '—', r.budgetMin > 0 ? `${Math.round(r.totalMin / r.budgetMin * 100)}%` : '—', r.workers, r.entries],
    ...Object.entries(r.byWorker).map(([wid, wd]) => {
      const w = workers.find(x => String(x.Id || x.id) === wid);
      const name = w ? `  ↳ ${w.FirstName || ''} ${w.LastName || ''}`.trim() : `  ↳ #${wid}`;
      return [name, fmtHHMM(wd.min), '', '', '', wd.count];
    }),
  ]);

  const exportCSV = () => {
    const lines = [HEADERS, ...expRows()].map(r => r.map(c => '"' + String(c).replace(/"/g, '""') + '"').join(','));
    const a = document.createElement('a'); a.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(lines.join('\n'));
    a.download = `project-hours-${todayStr()}.csv`; a.click();
  };
  const exportExcel = () => {
    const ws = XLSX.utils.aoa_to_sheet([HEADERS, ...expRows()]);
    ws['!cols'] = [28, 12, 12, 12, 12, 10].map(w => ({ wch: w }));
    const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, t('report.projectHours'));
    XLSX.writeFile(wb, `project-hours-${todayStr()}.xlsx`);
  };
  const exportPDF = () => {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    doc.setFont('helvetica', 'bold'); doc.setFontSize(13); doc.text(t('reports.projectHours'), 14, 15);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.text(`${t('report.period')}: ${dateFrom} — ${dateTo}`, 14, 22);
    autoTable(doc, { head: [HEADERS], body: rows.map(r => [r.name, fmtHHMM(r.totalMin), r.budgetMin > 0 ? fmtHHMM(r.budgetMin) : '—', r.budgetMin > 0 ? `${Math.round(r.totalMin / r.budgetMin * 100)}%` : '—', r.workers, r.entries]), startY: 27, styles: { fontSize: 8 }, headStyles: { fillColor: [124, 58, 237] } });
    doc.save(`project-hours-${todayStr()}.pdf`);
  };

  return (
    <ReportPage>
      <PageHeader title={t('reports.projectHours')} subtitle={t('report.projectHoursSubtitle')} />

      <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
          <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          <Typography variant="subtitle2" fontWeight={700}>{t('report.filters')}</Typography>
          {fProject && <Chip size="small" label={t('report.clear')} onClick={() => setFProject('')} onDelete={() => setFProject('')} />}
          <Chip size="small" label={t('report.refresh')} onClick={load} variant="outlined" sx={{ ml: 'auto' }} />
        </Stack>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={4}><TextField type="date" size="small" fullWidth label={t('report.from')} InputLabelProps={{ shrink: true }} value={dateFrom} onChange={e => setDateFrom(e.target.value)} /></Grid>
          <Grid item xs={6} sm={4}><TextField type="date" size="small" fullWidth label={t('report.to')} InputLabelProps={{ shrink: true }} value={dateTo} onChange={e => setDateTo(e.target.value)} /></Grid>
          <Grid item xs={12} sm={4}>
            <TextField select size="small" fullWidth label={t('report.project')} value={fProject} onChange={e => setFProject(e.target.value)}>
              <MenuItem value=""><em>{t('report.allProjects')}</em></MenuItem>
              {projects.map(p => <MenuItem key={p.id || p.Id} value={p.id || p.Id}>{p.name || p.Name}</MenuItem>)}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: t('report.projects'),   val: stats.projects,          color: 'primary.main'   },
          { label: t('report.totalHours'), val: fmtHHMM(stats.totalH),   color: 'info.main'      },
          { label: t('report.budgetHours'), val: fmtHHMM(stats.budgetH),  color: 'secondary.main' },
          { label: t('report.entries'),      val: stats.entries,            color: 'success.main'   },
        ].map(c => (
          <Grid item xs={6} sm={3} key={c.label}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, textAlign: 'center' }}>
              <Typography variant="h5" fontWeight={800} color={c.color}>{c.val}</Typography>
              <Typography variant="caption" color="text.secondary">{c.label}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <FolderOpenIcon sx={{ fontSize: 18, color: 'secondary.main' }} />
            <Typography variant="subtitle2" fontWeight={700}>{t('report.hoursByProject')}</Typography>
            <Chip size="small" label={`${rows.length} ${t('report.projects')}`} sx={{ fontSize: '0.72rem' }} />
          </Stack>
          <Stack direction="row" spacing={1}>
            <ExportMenu onCsv={exportCSV} onExcel={exportExcel} onPdf={exportPDF} />
          </Stack>
        </Stack>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 36 }} />
                {HEADERS.map(h => (
                  <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.78rem', bgcolor: 'grey.50', whiteSpace: 'nowrap' }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 5 }}><CircularProgress size={26} /></TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 5, color: 'text.secondary' }}>{t('report.noData')}</TableCell></TableRow>
              ) : rows.map(r => (
                <ProjectRow key={r.pid} proj={r} workers={workers} t={t} />
              ))}
            </TableBody>
          </Table>
        </Box>
        {rows.length > 0 && (
          <Box sx={{ px: 2, py: 1, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
            <Stack direction="row" spacing={3}>
              <Typography variant="caption" color="text.secondary">{t('report.total')}: <strong>{fmtHHMM(stats.totalH)}</strong></Typography>
              {stats.budgetH > 0 && <Typography variant="caption" color="text.secondary">{t('report.budget')}: <strong>{fmtHHMM(stats.budgetH)}</strong></Typography>}
            </Stack>
          </Box>
        )}
      </Paper>
    </ReportPage>
  );
}
